import type { RecordingRecoveryAction } from '@/components/meetings/RecordingRecoveryDialog'
import type { RecordingInfo } from '@/lib/utils/tauri-recording'
import { useEffect, useRef, useState } from 'react'
import { RecordingRecoveryDialog } from '@/components/meetings/RecordingRecoveryDialog'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { meetingRecordingsApi } from '@/lib/api/meeting-recordings.api'
import { createLogger } from '@/lib/logger'
import { isTauri } from '@/lib/utils/tauri'
import {
  checkRecordingPermissions,
  deleteLocalRecording,
  getRecordingState,
  listenForCaptionStatus,
  listenForRecordingAutoStopped,
  listenForRecordingCaption,
  listenForRecordingError,
  listenForRecordingLevel,
  listenForRecordingOrphans,
  listenForRecordingState,
  listenForRecordingSystemAudio,
  listenForRecordingUploadComplete,
  listenForRecordingUploadProgress,
  listLocalRecordings,
  openRecordingPrivacySettings,
  resolveLiveCaptionsEnabled,
  startRecording,
  uploadRecording,
} from '@/lib/utils/tauri-recording'
import {
  announceRecordingStarted,
  preflightRecordingStart,
  reportPreflightFailure,
} from '@/modules/meeting-recorder'
import { useOrganizationStore } from '@/store/organization.store'
import { useRecordingConsentStore } from '@/store/recording-consent.store'
import {
  clearStoredScratchpad,
  getStoredScratchpad,
  useRecordingStore,
} from '@/store/recording.store'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { MEETING_RECORDER_ERROR_CODES } from '@scrambl/shared'

const logger = createLogger('recorder-controller')

/** A local recording belongs to the open workspace (or predates the
 *  workspace stamp, in which case the open one is the best guess). */
function belongsToCurrentWorkspace(recording: RecordingInfo): boolean {
  const currentId = useOrganizationStore.getState().currentOrganization?.id
  return !recording.organizationId || recording.organizationId === currentId
}

/**
 * Headless controller for the desktop meeting recorder. Mounted once in
 * MainLayout (desktop only). Mirrors Rust recorder state into the zustand
 * store, surfaces errors, handles orphan-recovery and auto-upload on stop.
 */
export function RecorderController() {
  const startedUploadsRef = useRef(new Set<string>())
  const { checkFeatureFlag } = useFeatureFlags()
  const isMeetingNotesEnabled = checkFeatureFlag('MEETING_NOTES')
  const queryClient = useQueryClient()
  const currentOrganizationId = useOrganizationStore(
    state => state.currentOrganization?.id,
  )
  const checkForInterruptedRecordingRef = useRef(async () => {})
  // Set by the listener effect so the recovery dialog shares its upload path.
  const uploadRef = useRef<(recording: RecordingInfo) => Promise<void>>(
    async () => {},
  )
  // Interrupted recordings already asked about this launch ("decide later").
  const promptedRecoveryRef = useRef(new Set<string>())
  const [recovery, setRecovery] = useState<RecordingInfo | null>(null)
  const [recoveryBusy, setRecoveryBusy] =
    useState<RecordingRecoveryAction | null>(null)

  // Back in the workspace of a recording the app quit or crashed during:
  // offer to keep recording, finish it, or discard it.
  const checkForInterruptedRecording = async () => {
    if (!isTauri() || !isMeetingNotesEnabled || !currentOrganizationId) return
    if (useRecordingStore.getState().state !== 'idle') return
    try {
      const local = await listLocalRecordings()
      const interrupted = local.find(
        recording =>
          recording.interrupted &&
          recording.state === 'recorded' &&
          belongsToCurrentWorkspace(recording) &&
          !promptedRecoveryRef.current.has(recording.id),
      )
      if (interrupted) setRecovery(interrupted)
    } catch (error) {
      logger.error('Failed to check for interrupted recordings', error)
    }
  }

  // The startup orphan event fires inside the long-lived listener effect;
  // it reaches the latest check through this ref.
  useEffect(() => {
    checkForInterruptedRecordingRef.current = checkForInterruptedRecording
  })

  useEffect(() => {
    void checkForInterruptedRecording()
    // Re-check whenever the user returns to a workspace.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganizationId, isMeetingNotesEnabled])

  const closeRecovery = () => {
    if (recovery) promptedRecoveryRef.current.add(recovery.id)
    setRecovery(null)
  }

  const handleRecoveryAction = async (action: RecordingRecoveryAction) => {
    if (!recovery) return
    const recording = recovery
    setRecoveryBusy(action)
    try {
      if (action === 'discard') {
        await deleteLocalRecording(recording.id)
        clearStoredScratchpad(recording.id)
        useRecordingStore.getState().removePendingUpload(recording.id)
        closeRecovery()
        toast.success('Recording discarded')
        return
      }
      if (action === 'finish') {
        closeRecovery()
        void uploadRef.current(recording)
        return
      }
      // Keep recording: the same checks as any start, then continue the
      // same local recording (audio, notes and settings carry over).
      const preflight = await preflightRecordingStart({
        queryClient,
        organizationId: recording.organizationId ?? currentOrganizationId,
        surface: 'desktop',
      })
      if (!preflight.ok) {
        reportPreflightFailure(preflight)
        return
      }
      const permissions = await checkRecordingPermissions()
      if (permissions.microphone !== 'granted') {
        toast.error('Microphone access is off', {
          description:
            'Allow microphone access for Scrambl in System Settings, then keep recording.',
          action: {
            label: 'Open Settings',
            onClick: () => void openRecordingPrivacySettings('microphone'),
          },
        })
        return
      }
      await startRecording({
        resumeId: recording.id,
        organizationId: recording.organizationId ?? undefined,
        liveCaptions: await resolveLiveCaptionsEnabled(),
      })
      closeRecovery()
      announceRecordingStarted({
        title: 'Recording resumed',
        isOutOfCredits: preflight.isOutOfCredits,
      })
    } catch (error) {
      toast.error(
        action === 'discard'
          ? 'Could not discard the recording'
          : 'Could not continue the recording',
        {
          description: String(
            error instanceof Error ? error.message : error,
          ).slice(0, 200),
        },
      )
    } finally {
      setRecoveryBusy(null)
    }
  }

  useEffect(() => {
    if (!isTauri() || !isMeetingNotesEnabled) return

    let disposed = false
    const unlisteners: (() => void)[] = []

    // Refresh the persistent "pending uploads" list from disk so any
    // recording that hasn't reached the cloud (failed/interrupted upload, or
    // never started — survives crashes AND app updates via app_data_dir) is
    // discoverable and retryable, not just via a one-time startup toast.
    const refreshPending = async () => {
      try {
        const local = await listLocalRecordings()
        if (!disposed) useRecordingStore.getState().setPendingUploads(local)
      } catch (error) {
        logger.error('Failed to list local recordings', error)
      }
    }

    const handleUpload = async (recording: RecordingInfo | string) => {
      const id = typeof recording === 'string' ? recording : recording.id
      // Upload into the recording's own workspace, not whichever is open.
      const organizationId =
        typeof recording === 'string'
          ? useRecordingStore
              .getState()
              .pendingUploads.find(pending => pending.id === id)?.organizationId
          : recording.organizationId
      // Skip recordings the user discarded — delete the local file instead.
      if (useRecordingStore.getState().isDiscarded(id)) {
        clearStoredScratchpad(id)
        void deleteLocalRecording(id)
          .catch(() => {})
          .finally(() => useRecordingStore.getState().clearDiscarded(id))
        return
      }
      if (startedUploadsRef.current.has(id)) return
      startedUploadsRef.current.add(id)
      try {
        await uploadRecording(id, organizationId)
      } catch (error) {
        startedUploadsRef.current.delete(id)
        logger.error('Recording upload failed', error)
        // Keep it visible/retryable in the persistent list, not just here.
        void refreshPending()
        // Recorded by an app build without the consent pre-flight (or the
        // notice version changed): the audio is safe on disk — acknowledge
        // the notice, then the same local recording uploads.
        if (
          String(error instanceof Error ? error.message : error).includes(
            MEETING_RECORDER_ERROR_CODES.CONSENT_REQUIRED,
          )
        ) {
          toast.warning('Review the recording notice to upload', {
            description:
              'Your recording is saved on this computer. Acknowledge the notice and it uploads.',
            action: {
              label: 'Review notice',
              onClick: () => {
                void useRecordingConsentStore
                  .getState()
                  .request('desktop')
                  .then(acknowledged => {
                    if (acknowledged) void handleUpload(id)
                  })
              },
            },
            duration: 60_000,
          })
          return
        }
        toast.error('Recording upload failed', {
          description: String(
            error instanceof Error ? error.message : error,
          ).slice(0, 200),
          action: {
            label: 'Retry',
            onClick: () => void handleUpload(id),
          },
          duration: 30_000,
        })
      }
    }

    uploadRef.current = handleUpload

    const setup = async () => {
      const store = useRecordingStore.getState()

      // Hydrate from Rust (handles window reloads mid-recording)
      const state = await getRecordingState()
      if (state && !disposed) {
        store.applyStatePayload({
          state: state.state,
          recordingId: state.recordingId,
          startedAt: state.startedAt,
          durationMs: state.durationMs,
        })
      }

      // Surface any locally-stored recordings that haven't reached the cloud
      // (kept across crashes and app updates) so they're always retryable.
      void refreshPending()

      unlisteners.push(
        await listenForRecordingState(payload => {
          useRecordingStore.getState().applyStatePayload(payload)
        }),
      )
      unlisteners.push(
        await listenForRecordingLevel(payload => {
          useRecordingStore.getState().setLevels(payload)
        }),
      )
      unlisteners.push(
        await listenForRecordingError(payload => {
          useRecordingStore.getState().setError(payload)
          logger.error('Recording error', payload)
          toast.error('Recording error', { description: payload.message })
        }),
      )
      unlisteners.push(
        await listenForRecordingUploadProgress(payload => {
          useRecordingStore.getState().setUploadProgress(payload.recordingId, {
            uploadedBytes: payload.uploadedBytes,
            totalBytes: payload.totalBytes,
          })
        }),
      )
      unlisteners.push(
        await listenForRecordingUploadComplete(payload => {
          startedUploadsRef.current.delete(payload.recordingId)
          useRecordingStore.getState().removePendingUpload(payload.recordingId)
          // Ship the in-meeting scratchpad so the summarizer merges it. This
          // lands during transcription (minutes before summarize reads the
          // recording), so it always makes the summary.
          const scratchpad = getStoredScratchpad(payload.recordingId)
          if (scratchpad.trim()) {
            void meetingRecordingsApi
              .updateUserNotes(
                payload.apiRecordingId,
                scratchpad.slice(0, 50000),
              )
              .then(() => clearStoredScratchpad(payload.recordingId))
              .catch(error =>
                logger.error('Failed to attach scratchpad notes', error),
              )
          }
          toast.success('Recording uploaded', {
            description: 'AI notes are being generated — check Recordings.',
          })
        }),
      )
      unlisteners.push(
        await listenForRecordingSystemAudio(payload => {
          useRecordingStore.getState().setSystemAudioCaptured(payload.captured)
          if (!payload.captured && payload.reason === 'permission_denied') {
            toast.warning('Recording your microphone only', {
              description:
                'System audio is off, so other participants won’t be captured. Enable it to record everyone.',
              action: {
                label: 'Open Settings',
                onClick: () => void openRecordingPrivacySettings('systemAudio'),
              },
              duration: 12_000,
            })
          }
        }),
      )
      unlisteners.push(
        await listenForRecordingCaption(payload => {
          useRecordingStore.getState().appendCaption(payload)
        }),
      )
      unlisteners.push(
        await listenForCaptionStatus(payload => {
          useRecordingStore.getState().setCaptionsActive(
            payload.active
              ? true
              : payload.reason === 'starting'
                ? 'starting'
                : // Turned off mid-meeting: back to "not requested"
                  payload.reason === 'stopped'
                  ? null
                  : false,
          )
        }),
      )
      unlisteners.push(
        await listenForRecordingAutoStopped(() => {
          toast.info('Recording reached the 2-hour limit', {
            description:
              'Stopping and generating notes from what was captured.',
          })
        }),
      )
      unlisteners.push(
        await listenForRecordingOrphans(all => {
          if (!all.length) return
          useRecordingStore.getState().setPendingUploads(all)
          // Interrupted mid-meeting: the recovery dialog asks (in their own
          // workspace). Only finished, never-uploaded ones are offered here.
          if (all.some(recording => recording.interrupted)) {
            void checkForInterruptedRecordingRef.current()
          }
          const orphans = all.filter(
            recording =>
              !recording.interrupted && belongsToCurrentWorkspace(recording),
          )
          if (!orphans.length) return
          toast.info(
            orphans.length === 1
              ? 'A recording from a previous session was not uploaded'
              : `${orphans.length} recordings from previous sessions were not uploaded`,
            {
              action: {
                label: 'Upload',
                onClick: () => {
                  for (const orphan of orphans) void handleUpload(orphan)
                },
              },
              cancel: {
                label: 'Discard',
                onClick: () => {
                  for (const orphan of orphans) {
                    void deleteLocalRecording(orphan.id).then(() =>
                      useRecordingStore
                        .getState()
                        .removePendingUpload(orphan.id),
                    )
                  }
                },
              },
              duration: 60_000,
            },
          )
        }),
      )

      // When a recording finishes ("recorded" state on disk), upload it.
      // The state event fires on transition to idle; check local files then.
      unlisteners.push(
        await listenForRecordingState(payload => {
          if (payload.state !== 'idle') return
          void listLocalRecordings().then(local => {
            for (const recording of local) {
              // Interrupted ones wait for keep/finish; other workspaces' ones
              // upload when the user is back there.
              if (
                recording.state === 'recorded' &&
                !recording.interrupted &&
                belongsToCurrentWorkspace(recording)
              ) {
                void handleUpload(recording)
              }
            }
          })
        }),
      )
    }

    void setup().catch(error =>
      logger.error('Recorder controller setup failed', error),
    )

    return () => {
      disposed = true
      unlisteners.forEach(unlisten => unlisten())
    }
  }, [isMeetingNotesEnabled])

  return (
    <RecordingRecoveryDialog
      recording={recovery}
      hasNotes={
        recovery ? getStoredScratchpad(recovery.id).trim().length > 0 : false
      }
      busyAction={recoveryBusy}
      onAction={action => void handleRecoveryAction(action)}
      onLater={closeRecovery}
    />
  )
}
